import type {
  ChapterInput,
  ChapterOp,
  CourseChapter,
  CourseDetail,
  CourseListItem,
} from '@/api/course/courseType';
import { BookOpen } from 'lucide-vue-next';

export const COURSE_COLORS = [
  '#171717',
];

/** Shared chrome color for every course and subject detail page. */
export const COURSE_THEME_COLOR = COURSE_COLORS[0];

type EditableChapter = ChapterInput | CourseChapter;

export function getCourseColor(index: number) {
  return COURSE_COLORS[index % COURSE_COLORS.length];
}

export function toDynamicSubject(course: CourseListItem | CourseDetail, color: string) {
  return {
    id: String(course.id),
    id_: String(course.id),
    name: course.name,
    subjectName: course.name,
    description: course.description || '',
    color,
    teacher: course.teacher_name || '',
    icon: BookOpen,
    isCustomCourse: true,
    // 动态课程默认不展示依赖学科专属内容的“每周一思”。需要时由课程配置显式开启。
    showWeeklyThought: false,
  };
}

export type DynamicCourseSubject = ReturnType<typeof toDynamicSubject>;

export const DYNAMIC_COURSE_CONTEXT_KEY = 'probfun_dynamic_course_context';
export const DYNAMIC_COURSE_RETURN_TO_KEY = 'probfun_dynamic_course_return_to';

function serializeDynamicSubject(subject: DynamicCourseSubject) {
  const { icon: _icon, ...rest } = subject;
  return rest;
}

export function saveDynamicCourseContext(subject: DynamicCourseSubject, returnTo: string) {
  if (typeof window === 'undefined')
    return;
  sessionStorage.setItem(DYNAMIC_COURSE_CONTEXT_KEY, JSON.stringify(serializeDynamicSubject(subject)));
  sessionStorage.setItem(DYNAMIC_COURSE_RETURN_TO_KEY, returnTo);
}

export function restoreDynamicCourseContext(): DynamicCourseSubject | null {
  if (typeof window === 'undefined')
    return null;
  try {
    const raw = sessionStorage.getItem(DYNAMIC_COURSE_CONTEXT_KEY);
    if (!raw)
      return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.id_ || !parsed?.name)
      return null;
    return {
      ...parsed,
      // Ignore colors serialized by older builds so a stale session cannot
      // bring back subject-specific page chrome.
      color: COURSE_THEME_COLOR,
      icon: BookOpen,
      isCustomCourse: true,
      showWeeklyThought: false,
    };
  }
  catch {
    return null;
  }
}

export function clearDynamicCourseContext() {
  if (typeof window === 'undefined')
    return;
  sessionStorage.removeItem(DYNAMIC_COURSE_CONTEXT_KEY);
  sessionStorage.removeItem(DYNAMIC_COURSE_RETURN_TO_KEY);
}

function normalizeKnowledgePoints(chapter: ChapterInput) {
  chapter.knowledge_points = (chapter.knowledge_points ?? [])
    .filter(kp => kp.name.trim())
    .map((kp, index) => ({
      id: kp.id,
      name: kp.name.trim(),
      description: kp.description?.trim() ?? '',
      order: index,
    }));
  chapter.children = (chapter.children ?? []).map((child, index) => {
    child.order = index;
    normalizeKnowledgePoints(child);
    return child;
  });
}

export function normalizeChapters(chapters: ChapterInput[]) {
  return chapters.map((chapter, index) => {
    chapter.order = index;
    normalizeKnowledgePoints(chapter);
    return chapter;
  });
}

function flattenChapters(chapters: EditableChapter[]) {
  const map = new Map<number, EditableChapter>();
  function visit(chapter: EditableChapter) {
    if (chapter.id)
      map.set(chapter.id, chapter);
    chapter.children.forEach(visit);
  }
  chapters.forEach(visit);
  return map;
}

export function buildUpdateOps(original: CourseDetail, nextChapters: ChapterInput[]) {
  const ops: ChapterOp[] = [];
  const originalMap = flattenChapters(original.chapters);
  const nextMap = flattenChapters(nextChapters);

  function visit(chapter: ChapterInput, parentId: number | null) {
    if (!chapter.id) {
      ops.push({
        op: 'add',
        parent_id: parentId,
        name: chapter.name.trim(),
        description: chapter.description?.trim() ?? '',
        order: chapter.order,
        knowledge_points: chapter.knowledge_points,
        children: chapter.children,
      });
      return;
    }

    if (originalMap.has(chapter.id)) {
      ops.push({
        op: 'update',
        id: chapter.id,
        name: chapter.name.trim(),
        description: chapter.description?.trim() ?? '',
        order: chapter.order,
      });
      ops.push({
        op: 'set_kps',
        id: chapter.id,
        knowledge_points: chapter.knowledge_points,
      });
    }
    chapter.children.forEach(child => visit(child, chapter.id ?? null));
  }

  nextChapters.forEach(chapter => visit(chapter, null));

  for (const [id, chapter] of originalMap.entries()) {
    if (!nextMap.has(id) && !('is_protected' in chapter && chapter.is_protected)) {
      ops.push({ op: 'delete', id });
    }
  }

  return ops;
}
